import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY, extractError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (t) => {
    try {
      const res = await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(res.data);
      return true;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetchMe(token).finally(() => setLoading(false));
  }, [token, fetchMe]);

  const login = useCallback(
    async (username, password) => {
      try {
        const res = await api.post("/auth/login", { username, password });
        const t = res.data.access_token;
        localStorage.setItem(TOKEN_KEY, t);
        setToken(t);
        const ok = await fetchMe(t);
        return { ok };
      } catch (err) {
        return { ok: false, error: extractError(err, "Login failed") };
      }
    },
    [fetchMe]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    refresh: () => token && fetchMe(token),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
